import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Target, Shield, TrendingUp } from 'lucide-react';
export function CJIPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-219">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-220">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-221">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-222">
            <span className="w-8 h-px bg-teal" data-id="element-223"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-224">
              Civic Judgment Index
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-225"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-226">
            The Analytical Core of Civic Judgment
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-227">
            The Civic Judgment Index (CJI) is the foundational analytical layer
            of the SyNeural platform. It is designed to measure and strengthen
            reasoning quality, decision competence, manipulation resistance, and
            leadership judgment across civic, institutional, and
            high-responsibility environments.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-228">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="prose prose-lg max-w-none" data-id="element-229">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-230">
            Modern societies rely on judgment everywhere, but rarely have a
            clear framework for evaluating it. People are expected to make
            decisions under uncertainty, sort signal from noise, resist
            emotional manipulation, and act with foresight in environments
            shaped by complexity, pressure, and competing narratives.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-231">
            CJI was created as the foundational analytical structure for
            understanding how people reason, how they respond to persuasion and
            pressure, and how decision quality can be strengthened over time.
          </p>
        </motion.div>
      </section>

      {/* Why CJI Exists */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-232">
        <div className="max-w-4xl mx-auto" data-id="element-233">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-234">
            <div className="flex items-center gap-4 mb-6" data-id="element-235">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-236">
                <Target size={24} data-id="element-237" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-238">
                Why CJI Exists
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-239">
              Judgment is one of the most important variables in modern society,
              yet institutions often evaluate credentials, outputs, or
              compliance without examining the underlying quality of reasoning
              that shapes decisions. Weak foresight, emotional reactivity, and
              manipulation vulnerability can create consequences far beyond a
              single moment. CJI exists to provide a more structured way to
              assess the core dimensions of judgment that matter in civic,
              professional, institutional, and leadership settings.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What CJI Measures */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-240">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-241">
          <div className="flex items-center gap-4 mb-6" data-id="element-242">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-243">
              <Brain size={24} data-id="element-244" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-245">
              What CJI Measures
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed mb-8" data-id="element-246">
            CJI focuses on the key dimensions that shape civic judgment and
            practical decision-making. These include:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-247">
            {[{
            name: 'Reasoning quality',
            link: '#reasoning-quality'
          }, {
            name: 'Consequential thinking',
            link: '#consequential-thinking'
          }, {
            name: 'Manipulation resistance',
            link: '#manipulation-resistance'
          }, {
            name: 'Emotional regulation',
            link: '#emotional-regulation'
          }, {
            name: 'Social cooperation',
            link: '#social-cooperation'
          }, {
            name: 'Leadership responsibility',
            link: '#leadership-responsibility'
          }].map((item, index) => <motion.a key={item.name} href={item.link} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} className="flex items-center gap-3 bg-white p-4 rounded-lg border border-line hover:border-navy hover:shadow-md transition-all group" data-id="element-248">
                <div className="w-2 h-2 rounded-full bg-navy" data-id="element-249"></div>
                <span className="text-ink-dark font-medium group-hover:text-navy transition-colors" data-id="element-250">
                  {item.name}
                </span>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-navy" data-id="element-251">
                  <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-252" />
                </svg>
              </motion.a>)}
          </div>
          <p className="text-lg text-ink-muted leading-relaxed mt-8" data-id="element-253">
            Together, these domains help create a more rigorous picture of how
            judgment functions under complexity, pressure, and responsibility.
          </p>
        </motion.div>
      </section>

      {/* The Role of CJI */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-254">
        <div className="max-w-4xl mx-auto" data-id="element-255">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-256">
            <div className="flex items-center gap-4 mb-6" data-id="element-257">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-258">
                <Shield size={24} data-id="element-259" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-260">
                The Role of CJI in the Platform
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-261">
              CJI serves as the analytical base layer of SyNeural. It is the
              platform's core engine for decision-quality analytics,
              propaganda-resistance profiling, cognitive pattern assessment, and
              leadership reasoning diagnostics. Where NCJI expands into broader
              developmental pathways, lifecycle architecture, and institutional
              deployment, CJI provides the essential measurement logic that
              makes those higher-order applications possible.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How CJI Creates Value */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-262">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-263">
          <div className="flex items-center gap-4 mb-6" data-id="element-264">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-265">
              <TrendingUp size={24} data-id="element-266" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-267">
              How CJI Creates Value
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-268">
            CJI is designed to make judgment more visible, more structured, and
            more actionable. It helps individuals, institutions, and leadership
            environments better understand where reasoning is strong, where
            vulnerabilities exist, and where development is needed. By
            identifying patterns, clarifying risks, and supporting stronger
            decision-making, CJI creates a more useful analytical foundation for
            growth and readiness.
          </p>
        </motion.div>
      </section>

      {/* Use Cases */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-269">
        <div className="max-w-5xl mx-auto" data-id="element-270">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-271">
            <h2 className="text-3xl font-heading font-bold text-ink-dark mb-12 text-center" data-id="element-272">
              Use Cases
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-273">
              {[{
              title: 'Education',
              desc: 'Support reasoning, media literacy, and judgment development',
              link: '#education'
            }, {
              title: 'Employers & Organizations',
              desc: 'Strengthen decision quality, communication, and advancement readiness',
              link: '#employers'
            }, {
              title: 'Civil Society',
              desc: 'Support misinformation resilience and collective reasoning capacity',
              link: '#civil-society'
            }, {
              title: 'Government',
              desc: 'Structured framework for understanding readiness and judgment quality',
              link: '#government'
            }, {
              title: 'Leadership',
              desc: 'High-responsibility decision-making and executive evaluation',
              link: '#leadership'
            }].map((useCase, index) => <motion.a key={useCase.title} href={useCase.link} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-white p-6 rounded-lg border border-line hover:border-navy hover:shadow-md transition-all group" data-id="element-274">
                  <h3 className="text-lg font-heading font-bold text-ink-dark mb-2 group-hover:text-navy transition-colors" data-id="element-275">
                    {useCase.title}
                  </h3>
                  <p className="text-ink-muted text-sm leading-relaxed mb-3" data-id="element-276">
                    {useCase.desc}
                  </p>
                  <div className="flex items-center gap-1 text-navy text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-277">
                    Learn More
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-278">
                      <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-279" />
                    </svg>
                  </div>
                </motion.a>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CJI and NCJI */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-280">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-navy-deep text-white p-12 rounded-2xl" data-id="element-281">
          <h2 className="text-3xl font-heading font-bold mb-6" data-id="element-282">CJI and NCJI</h2>
          <p className="text-lg leading-relaxed opacity-90" data-id="element-283">
            Within SyNeural's broader ecosystem, CJI serves as the core
            analytical layer. NCJI builds on that foundation to create a wider
            developmental, institutional, and leadership-readiness system.
            Together, they form a unified architecture for understanding,
            strengthening, and scaling judgment across the full human and
            institutional spectrum.
          </p>
        </motion.div>
      </section>
    </div>;
}